import React, { useState, useEffect } from "react";
import { decode } from "blurhash";
import { cn } from "@/lib/utils";

interface BlurhashImageProps {
  src: string;
  blurhash?: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
  loading?: "lazy" | "eager";
  onLoad?: () => void;
  onError?: () => void;
}

export function BlurhashImage({
  src,
  blurhash,
  alt,
  width = 400,
  height = 300,
  className,
  objectFit = "cover",
  loading = "lazy",
  onLoad,
  onError,
}: BlurhashImageProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [blurhashUrl, setBlurhashUrl] = useState<string | null>(null);

  // generate blurhash placeholder
  useEffect(() => {
    if (!blurhash || imageLoaded) return;

    try {
      const pixels = decode(blurhash, 32, 32);
      const canvas = document.createElement("canvas");
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        const imageData = ctx.createImageData(32, 32);
        imageData.data.set(pixels);
        ctx.putImageData(imageData, 0, 0);
        setBlurhashUrl(canvas.toDataURL());
      }
    } catch (error) {
      console.warn("Failed to decode blurhash:", error);
    }
  }, [blurhash, imageLoaded]);

  const handleImageLoad = () => {
    console.log("BlurhashImage: Successfully loaded image:", src);
    setImageLoaded(true);
    onLoad?.();
  };

  const handleImageError = () => {
    console.error("BlurhashImage: Failed to load image:", src);
    setImageError(true);
    onError?.();
  };

  // if no src provided or image error, show placeholder
  if (!src || imageError) {
    return (
      <div
        className={cn(
          "bg-muted flex items-center justify-center text-muted-foreground text-sm",
          className,
        )}
        style={{ width, height }}
      >
        {!src ? "No image" : "Failed to load image"}
      </div>
    );
  }

  const isAbsolute = className?.includes("absolute");
  const containerStyle = isAbsolute ? {} : { width, height };

  return (
    <div
      className={cn("relative overflow-hidden", className)}
      style={containerStyle}
    >
      {/* blurhash placeholder */}
      {blurhashUrl && !imageLoaded && (
        <img
          src={blurhashUrl}
          alt=""
          className="absolute inset-0 w-full h-full"
          style={{
            objectFit,
            filter: "blur(0px)",
            transition: "opacity 0.3s ease-in-out",
          }}
        />
      )}

      {/* fallback placeholder if no blurhash */}
      {!blurhashUrl && !imageLoaded && (
        <div className="absolute inset-0 bg-muted animate-pulse w-full h-full" />
      )}

      {/* actual image */}
      <img
        src={src}
        alt={alt}
        loading={loading}
        onLoad={handleImageLoad}
        onError={handleImageError}
        className={cn(
          "w-full h-full transition-opacity duration-300",
          imageLoaded ? "opacity-100" : "opacity-0",
        )}
        style={{ objectFit }}
      />
    </div>
  );
}
